import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ArrowBack,
    Launch,
    ContentCopy,
    Delete,
    CalendarToday,
    Timer,
    Person,
} from '@mui/icons-material';
import {copyToClipboard, formatRelativeTime2} from '@/utils/helpers';

import { toast } from 'react-toastify';
import { meetingService } from '@services/api';
import MeetingSummary from "../Summary/MeetingSummary"; // Adjust import as needed
import { useTranslation } from "@/hooks/useTranslation";
export const STATUS_COLORS = {
    SCHEDULED: 'primary',
    STARTED: 'success',
    ENDED: 'default',
    CANCELLED: 'error'
};
const MeetingDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [meeting, setMeeting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMeeting = async () => {
            try {
                setLoading(true);
                const response = await meetingService.getMeetingById(id);
                setMeeting(response.data);
            } catch (err) {
                console.error('Error fetching meeting:', err);
                setError(t("interviews.details.loadFailed"));
                toast.error(t("interviews.details.loadFailed"));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchMeeting();
        }
    }, [id]);

    const handleCopyLink = async () => {
        if (meeting?.joinUrl) {
            const success = await copyToClipboard(meeting.joinUrl);
            if (success) {
                toast.success(t("interviews.toasts.linkCopied"));
            }
        }
    };

    const handleJoinMeeting = () => {
        if (meeting?.joinUrl) {
            window.open(meeting.joinUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t("interviews.card.deleteConfirm", { topic: meeting?.topic }))) {
            try {
                await meetingService.deleteMeeting(id);
                toast.success(t("interviews.toasts.deleteSuccess"));
                navigate('/interviews');
            } catch (err) {
                toast.error(t("interviews.toasts.deleteFailed"));
            }
        }
    };
    const [endingMeeting, setEndingMeeting] = useState(false); // new state
    const handleEndMeeting = async () => {
        if (window.confirm(t("interviews.details.endMeetingConfirm"))) {
            try {
                setEndingMeeting(true); // start loading
                await meetingService.updateMeetingStatus(meeting.id, 'ENDED');
                toast.success(t("interviews.toasts.endMeetingSuccess"));

                // Refetch updated meeting details
                const response = await meetingService.getMeetingById(meeting.id);
                setMeeting(response.data);
            } catch (error) {
                toast.error(t("interviews.toasts.endMeetingFailed"));
            } finally {
                setEndingMeeting(false); // stop loading
            }
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !meeting) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="error" gutterBottom>
                        {error || t("interviews.details.notFound")}
                    </Typography>
                    <Button startIcon={<ArrowBack className="rtl:-scale-x-100" />} onClick={() => navigate('/interviews')}>
                        {t("interviews.details.backToMeetings")}
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="fullWidth" sx={{ py: 4 }}>
            <Button
                startIcon={<ArrowBack className="rtl:-scale-x-100" />}
                onClick={() => navigate('/interviews')}
                sx={{ mb: 3 }}
            >
                {t("interviews.details.backToMeetings")}
            </Button>

            <Paper sx={{ p: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography variant="h5" gutterBottom fontWeight="600">
                            {meeting.topic}
                        </Typography>
                        <Chip
                            label={meeting.status}
                            color={STATUS_COLORS[meeting.status]}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {meeting.status === 'SCHEDULED' && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleEndMeeting}
                                disabled={endingMeeting} // block multiple clicks
                                startIcon={endingMeeting ? <CircularProgress size={20} /> : null} // show spinner
                            >
                                {endingMeeting ? t("interviews.details.ending") : t("interviews.details.endMeeting")}
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<Launch />}
                            onClick={() => window.open(meeting.joinUrl, '_blank')}
                            disabled={meeting.status === 'CANCELLED'}
                        >
                            {t("interviews.details.joinMeeting")}
                        </Button>
                    </Box>
                </Box>

                {meeting.agenda && (
                    <>
                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                            {t("interviews.details.agendaLabel")}
                        </Typography>
                        <Typography color="text.secondary" paragraph>
                            {meeting.agenda}
                        </Typography>
                    </>
                )}

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box display="flex" alignItems="center">
                        <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {t("interviews.details.startTimeLabel")}
                            </Typography>
                            <Typography>{formatRelativeTime2(meeting.startTime)}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                        <Timer sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {t("interviews.details.durationLabel")}
                            </Typography>
                            <Typography>{t("focusGroups.card.durationValue", { count: meeting.duration })}</Typography>
                        </Box>
                    </Box>

                    {meeting.hostEmail && (
                        <Box display="flex" alignItems="center">
                            <Person sx={{ mr: 2, color: 'text.secondary' }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t("interviews.details.hostLabel")}
                                </Typography>
                                <Typography>{meeting.hostEmail}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {meeting.password && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>{t("interviews.card.passwordLabel")}</strong> {meeting.password}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={1}>
                        <Tooltip title={t("interviews.card.copyLink")}>
                            <IconButton color="primary" onClick={handleCopyLink}>
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("interviews.card.deleteMeeting")}>
                            <IconButton color="error" onClick={handleDelete}>
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Launch />}
                        onClick={handleJoinMeeting}
                        disabled={meeting.status === 'CANCELLED'}
                    >
                        {t("interviews.details.joinMeeting")}
                    </Button>
                </Box>
            </Paper>
            {meeting.status === 'ENDED' && (
                <Box sx={{ mt: 3 }}>
                    <MeetingSummary meetingId={meeting.id} />
                </Box>
            )}
        </Container>
    );
};

export default MeetingDetail;